import React, { useState } from 'react';
import axios from 'axios';
import RegistrationModal from './RegistrationModal';
import ForgotPasswordModal from './ForgotPasswordModal';

function Login({ onLogin, sessionExpiredMsg }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showAccessCode, setShowAccessCode] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [accessCodeLoading, setAccessCodeLoading] = useState(false);

  const handleSubmit = async (e) => {
  e.preventDefault();
  setError('');
  setLoading(true);

  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/login`, {
      username,
      password
    });

    if (response.data.success) {
      // Pass token, user, and refresh token
      onLogin(response.data.token, response.data.user, response.data.refreshToken);
    } else {
      setError('Invalid credentials');
    }
  } catch (err) {
    if (err.response) {
      // Server reached and responded with an error (e.g. 401 wrong username/password)
      setError(err.response.data?.message || 'Invalid username or password.');
    } else if (err.request) {
      // Request never got a response - network/proxy/VPN block, not a credentials issue
      setError(
        'Unable to reach the server. If you are on a VPN or hospital network, it may be blocking access to this site — try disabling the VPN, or contact IT if the problem continues.'
      );
    } else {
      setError('Login failed. Please try again.');
    }
    console.error('Login error:', err);
  } finally {
    setLoading(false);
  }
};

  const handleAccessCodeSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAccessCodeLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/access`, {
        code: accessCode
      });

      if (response.data.success) {
        onLogin(response.data.token, response.data.user, response.data.refreshToken);
      } else {
        setError('Access code not recognized.');
      }
    } catch (err) {
      if (err.response) {
        setError(err.response.data?.message || 'Access code not recognized.');
      } else if (err.request) {
        setError('Unable to reach the server. This may still be a network/VPN block.');
      } else {
        setError('Access failed. Please try again.');
      }
      console.error('Access code error:', err);
    } finally {
      setAccessCodeLoading(false);
    }
  };

  return (
    <>
      {showRegistration && (
        <RegistrationModal onClose={() => setShowRegistration(false)} />
      )}

      {showForgotPassword && (
        <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
      )}

      <div className="login-container">
        <div className="login-box">
          <h2>Ultrasound Worksheet Login</h2>
          {sessionExpiredMsg && (
            <div className="alert alert-danger" role="alert" style={{ marginBottom: '15px', fontSize: '0.9rem' }}>
              {sessionExpiredMsg}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Username:</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Password:</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && <div className="error-message">{error}</div>}
            <button type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          <div className="login-footer">
            <p>
              <button
                className="create-account-btn"
                onClick={() => setShowAccessCode(!showAccessCode)}
                type="button"
              >
                {showAccessCode ? 'Use username and password instead' : 'Have an access code instead?'}
              </button>
            </p>
            {showAccessCode && (
              <form onSubmit={handleAccessCodeSubmit} style={{ marginBottom: '10px' }}>
                <div className="form-group">
                  <label>Access Code:</label>
                  <input
                    type="text"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <button type="submit" disabled={accessCodeLoading}>
                  {accessCodeLoading ? 'Checking...' : 'Continue with Access Code'}
                </button>
              </form>
            )}
            <p>
              <button
                className="create-account-btn"
                onClick={() => setShowForgotPassword(true)}
                type="button"
              >
                Forgot username or password?
              </button>
            </p>
            <p>
              Don't have an account?{' '}
              <button
                className="create-account-btn"
                onClick={() => setShowRegistration(true)}
                type="button"
              >
                Create Account
              </button>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default Login;
